import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';

import Skeleton from 'components/Skeleton';
import { ElementNames, isTabletOrMobileDevice, SportIds } from 'constants/app';
import { lineFancyBranding, marketGraphsBranding } from 'constants/branding';
import { MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX } from 'constants/marketGraph';
import { BetSidesTypes, BettingTypes } from 'constants/markets';
import { useMarketUnits } from 'hooks/useMarketUnits';
import useOddsType from 'hooks/useOddsType';
import {
  getCurrency,
  getDisplayCurrencySymbol,
  getEnableFancyMarketsViewOnCricketForMarketGraphs,
  getEnableLineMarketsBrandingForMarketGraphs,
  getIsCurrencySymbolAfterAmount,
  getIsMarketGraphsDarkThemeEnabled,
  getMarketGraphIsSmallScreenView
} from 'redux/modules/appConfigs/selectors';
import { getElementHeightByName } from 'redux/modules/appSettings/selectors';
import { getMarketDefinitionMarketUnit } from 'redux/modules/marketDefinitions/selectors';
import {
  getGraphMarketBestBack,
  getGraphMarketBettingType,
  getGraphMarketDisabledLayOdds,
  getGraphMarketEventTypeId,
  getGraphMarketIsFancy,
  getIsMarketDataLoaded,
  getLiquidityTradedTable
} from 'redux/modules/marketGraph/selectors';
import { MarketGraphLiquidityTradedTableHeader } from 'types/marketGraph';
import { addCommasToNumber } from 'utils';
import { roundMillions } from 'utils/currency';
import { getOddsValue } from 'utils/marketGraph';

import MarketGraphNoData from '../MarketGraphNoData/MarketGraphNoData';

import commonStyles from '../MarketGraphCommon.module.scss';
import styles from './MarketGraphLiquidityTradedTable.module.scss';

export default function MarketGraphLiquidityTradedTable() {
  const { t } = useTranslation();
  const { marketId } = useParams();

  const isSmallScreenViewView = useSelector(getMarketGraphIsSmallScreenView);
  const headerHeight = useSelector(getElementHeightByName(ElementNames.MARKET_GRAPH_HEADER));
  const tableData = useSelector(getLiquidityTradedTable);
  const isMarketDataLoaded = useSelector(getIsMarketDataLoaded);
  const displayCurrencySymbol = useSelector(getDisplayCurrencySymbol);
  const currencySymbolAfterAmount = useSelector(getIsCurrencySymbolAfterAmount);
  const currency = useSelector(getCurrency);
  const isDarkTheme = useSelector(getIsMarketGraphsDarkThemeEnabled);
  const bettingType = useSelector(getGraphMarketBettingType);
  const eventTypeId = useSelector(getGraphMarketEventTypeId);
  const bestBack = useSelector(getGraphMarketBestBack);
  const isDisabledLayOdds = useSelector(getGraphMarketDisabledLayOdds);
  const enableFancyMarketsViewOnCricket = useSelector(getEnableFancyMarketsViewOnCricketForMarketGraphs);
  const enableLineMarketsBranding = useSelector(getEnableLineMarketsBrandingForMarketGraphs);
  const marketUnit = useSelector(getMarketDefinitionMarketUnit(marketId || ''));
  const marketUnitTranslated = useMarketUnits(marketUnit || 'points');
  const isFancyView = useSelector(getGraphMarketIsFancy);

  const [backBrandingColor, setBackBackgroundColor] = useState('');
  const [layBrandingColor, setLayBackgroundColor] = useState('');

  const isMobile = isSmallScreenViewView || isTabletOrMobileDevice;
  const isData = tableData.length;
  const isLoading = !isMarketDataLoaded;
  const isLineMarket = bettingType === BettingTypes.LINE;
  const isCricketMarket = String(eventTypeId) === SportIds.CRICKET;
  const isBeforeAmount = !currencySymbolAfterAmount && displayCurrencySymbol;
  const isAfterAmount = currencySymbolAfterAmount && displayCurrencySymbol;
  const currencySymbol = currency.symbol;

  const { isAmericanOdds } = useOddsType();

  const isFancyBranding = isCricketMarket && enableFancyMarketsViewOnCricket && isLineMarket && isFancyView;
  const isLineBranding = isLineMarket && enableLineMarketsBranding && !isFancyBranding;
  const isAmericanOddsEnabled = isAmericanOdds && !isLineMarket;
  let backLabelKey = 'marketGraph.labels.toBack';
  let layLabelKey = 'marketGraph.labels.toLay';

  if (isFancyBranding) {
    backLabelKey = 'betslip.labels.type.no';
    layLabelKey = 'betslip.labels.type.yes';
  } else if (isLineBranding) {
    backLabelKey = 'betslip.labels.type.under';
    layLabelKey = 'betslip.labels.type.over';
  }

  const columns: MarketGraphLiquidityTradedTableHeader[] = [
    { label: isLineMarket ? marketUnitTranslated : t('account.mybets.labels.odds'), id: 'odds' },
    { label: t(backLabelKey), id: BetSidesTypes.back },
    ...(!isDisabledLayOdds
      ? [{ label: t(layLabelKey), id: BetSidesTypes.lay } as MarketGraphLiquidityTradedTableHeader]
      : []),
    { label: t('inplay.labels.matched'), id: 'matched' }
  ];

  const backColorRef = useRef<HTMLDivElement>(null);
  const layColorRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const bestBackRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (bestBack && bestBackRef.current && tableRef.current) {
      if (isMobile) {
        const { top: bestBackTop = 0 } = bestBackRef.current.getBoundingClientRect();
        const { top: tableTop = 0 } = tableRef.current.getBoundingClientRect();

        tableRef.current.scrollTop = bestBackTop - tableTop - 150;
      } else {
        bestBackRef.current?.scrollIntoView({ block: 'center' });
      }
    }
  }, [bestBack]);

  useEffect(() => {
    if (backColorRef.current && layColorRef.current) {
      setBackBackgroundColor(getComputedStyle(backColorRef.current).getPropertyValue('background-color'));
      setLayBackgroundColor(getComputedStyle(layColorRef.current).getPropertyValue('background-color'));
    }
  }, []);

  return (
    <div
      className={classNames(styles.container, {
        [styles.container__mobile]: isTabletOrMobileDevice,
        [styles.container__smallScreen]: isSmallScreenViewView,
        [styles.container__dark]: isDarkTheme
      })}
    >
      <p
        className={classNames(commonStyles.title, {
          [commonStyles.title__mobile]: isTabletOrMobileDevice,
          [commonStyles.title__dark]: isDarkTheme
        })}
      >
        {t('marketGraph.labels.oddsAvailableMatched', {
          UNITS: isLineMarket && marketUnit ? marketUnitTranslated : t('account.mybets.labels.odds')
        })}
      </p>
      {isLoading ? (
        <>
          <Skeleton
            withBrandingClassName={false}
            className={classNames(commonStyles.skeletons__top, commonStyles.skeleton, {
              [commonStyles.skeletons__top__mobile]: isMobile,
              [commonStyles.skeleton__dark]: isDarkTheme
            })}
          />
          <Skeleton
            withBrandingClassName={false}
            className={classNames(commonStyles.skeletons__bottom, commonStyles.skeleton, {
              [commonStyles.skeletons__bottom__mobile]: isMobile,
              [commonStyles.skeleton__dark]: isDarkTheme
            })}
          />
        </>
      ) : (
        <>
          {isData ? (
            <div>
              <div className={styles.table__row}>
                {columns.map(({ label, id }) => {
                  return (
                    <p
                      key={id}
                      className={classNames(styles.table__cell, styles.table__cell__wrap, styles.table__cell__header, {
                        [styles.table__cell__header__dark]: isDarkTheme,
                        [styles.table__cell__oddsCol]: id === 'odds',
                        [styles.table__cell__odds]: id === 'odds',
                        [styles.table__cell__mobile]: isTabletOrMobileDevice,
                        [styles.table__cell__smallScreen]: isSmallScreenViewView
                      })}
                    >
                      {label}
                    </p>
                  );
                })}
              </div>
              <div
                // 202 - sum of height of all paddings, margins, gaps, footer and title height
                style={isMobile ? undefined : { maxHeight: `calc(100dvh - ${headerHeight + 202}px)` }}
                className={classNames(styles.table, {
                  [styles.table__mobile]: isTabletOrMobileDevice,
                  [styles.table__smallScreenView]: isSmallScreenViewView && !isTabletOrMobileDevice,
                  [styles.table__dark]: isDarkTheme
                })}
                ref={tableRef}
                role="table"
                tabIndex={MARKET_GRAPH_LIQUIDITY_TABLE_TAB_INDEX}
              >
                {tableData.map(row => {
                  return (
                    <div
                      key={`${row.odds}_${row.back}_${row.lay}_${row.matched}`}
                      role="row"
                      className={classNames(styles.table__row, styles.table__row__body, {
                        [styles.table__row__body__dark]: isDarkTheme
                      })}
                    >
                      {columns.map(({ id }) => {
                        const amount = row[id] ? addCommasToNumber(roundMillions(String(row[id]) ?? '', t)) : '';

                        const isBack = (id === BetSidesTypes.back || id === 'odds') && row.back !== '';
                        const isLay = (id === BetSidesTypes.lay || id === 'odds') && row.lay !== '';

                        const brandingStyles =
                          isDarkTheme && id !== 'matched'
                            ? {
                                color: isBack ? backBrandingColor : isLay ? layBrandingColor : '#ffffff'
                              }
                            : {};

                        return (
                          <p
                            className={classNames(styles.table__cell__wrap, styles.table__cell__body, {
                              [styles.table__cell__oddsCol]: id === 'odds',
                              [marketGraphsBranding.BACK_CELL]:
                                (!isDarkTheme || isLineBranding || isFancyBranding) && isBack,
                              [marketGraphsBranding.LAY_CELL]:
                                (!isDarkTheme || isLineBranding || isFancyBranding) && isLay,
                              [lineFancyBranding.biab_line]: isLineBranding,
                              [lineFancyBranding.biab_fancy]: isFancyBranding
                            })}
                            key={id}
                          >
                            <span
                              className={classNames(styles.table__cell, {
                                [marketGraphsBranding.CELL_CONTENT]: !isDarkTheme || isLineBranding || isFancyBranding,
                                [styles.table__cell__mobile]: isMobile,
                                [styles.table__cell__odds]: id === 'odds',
                                [styles.table__cell__bold]: id === 'odds',
                                [styles.table__cell__dark]: isDarkTheme,
                                [styles.table__cell__body__dark]: isDarkTheme
                              })}
                              style={brandingStyles}
                              ref={row.isBestBack ? bestBackRef : undefined}
                            >
                              {id === 'odds' ? (
                                getOddsValue({ odds: row[id], isAmericanOdds: isAmericanOddsEnabled })
                              ) : (
                                <>
                                  {isBeforeAmount && amount && (
                                    <span
                                      className={classNames(styles.table__cell__header, {
                                        [styles.table__cell__header__dark]: isDarkTheme
                                      })}
                                    >
                                      {currencySymbol}
                                    </span>
                                  )}
                                  {amount}
                                  {isAfterAmount && amount && (
                                    <span
                                      className={classNames(styles.table__cell__header, {
                                        [styles.table__cell__header__dark]: isDarkTheme
                                      })}
                                    >
                                      {currencySymbol}
                                    </span>
                                  )}
                                </>
                              )}
                            </span>
                          </p>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <MarketGraphNoData type="liquidity-table" />
          )}
        </>
      )}
      <span className={classNames(styles.hide, marketGraphsBranding.BACK_CELL)} ref={backColorRef} />
      <span className={classNames(styles.hide, marketGraphsBranding.LAY_CELL)} ref={layColorRef} />
    </div>
  );
}
